import { z } from 'zod';

import { skillTypeSchema } from '~/shared/data-contract/skill';

import { taskContentSchema } from './chat-blocks/change-proposal-block';

const changeProposalCompareSectionSchema = z.object({
    id: z.string(),
    tag: z.string(),
    value: z.string(),
});

const changeProposalCompareSectionTagSchema = z.object({
    id: z.string(),
    names: z.array(
        z.object({
            locale: z.string(),
            text: z.string(),
        }),
    ),
    type: z.string(),
});

const changeProposalCompareTranslationSchema = z.object({
    language: z.string(),
    structured: changeProposalCompareSectionSchema.array(),
    title: z.string(),
});

const changeProposalCompareSkillSchema = z.object({
    id: z.string(),
    competencyId: z.string().nullish(),
    level: z.number().int(),
    title: z.string(),
    type: skillTypeSchema.nullish(),
});

const changeProposalCompareCompetencySchema = z.object({
    id: z.string(),
    title: z.string(),
});

// Older snapshots carry titles only; every content field is optional there.
export const changeProposalCompareTaskSchema = taskContentSchema.partial({ requiredSkills: true }).extend({
    generationState: z.string().nullish(),
    id: z.string().nullish(),
    key: z.string().nullish(),
    order: z.number().int().nullish(),
});

export type ChangeProposalCompareTask = z.infer<typeof changeProposalCompareTaskSchema>;

export const changeProposalCompareProfileSchema = z.object({
    id: z.string(),
    competencies: changeProposalCompareCompetencySchema.array(),
    language: z.string(),
    sectionTags: changeProposalCompareSectionTagSchema.array(),
    skills: changeProposalCompareSkillSchema.array(),
    structured: changeProposalCompareSectionSchema.array(),
    tasks: changeProposalCompareTaskSchema.array().nullish(),
    title: z.string(),
    translations: changeProposalCompareTranslationSchema.array(),
    updatedAt: z.string(),
});

export type ChangeProposalCompareProfile = z.infer<typeof changeProposalCompareProfileSchema>;

export const changeProposalCompareTargetSchema = z.union([
    z.object({ itemId: z.string(), itemType: z.enum(['job-role', 'job-level']), jobArchitectureId: z.string() }),
    z.object({ itemId: z.string(), itemType: z.literal('position') }),
]);

export type ChangeProposalCompareTarget = z.infer<typeof changeProposalCompareTargetSchema>;

export const changeProposalCompareResponseSchema = z.object({
    blockId: z.uuid(),
    createdAt: z.string(),
    current: changeProposalCompareProfileSchema,
    expiresAt: z.string(),
    language: z.string(),
    proposed: changeProposalCompareProfileSchema,
    target: changeProposalCompareTargetSchema,
});

export type ChangeProposalCompareResponse = z.infer<typeof changeProposalCompareResponseSchema>;
